import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Heading from "../../../component/Heading/Heading";
import IntegrationModal from "./components/IntegrationModal";

// MUI Icons
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import deleteIcon from "../../../assets/icons/trash.svg";
import ApiIcon from "@mui/icons-material/Api";

import {
  getCustomerIntegration,
  addCustomerIntegration,
  updateCustomerIntegration,
  deleteCustomerIntegration,
} from "../../../services/customer-services/customerIntegrationService";

import "../../../styles/pages/customer/integrations.scss";
import CommonButton from "../../../component/button/Button";
import IntegrationItemActions from "./components/CustomerIntegrationActions";

const ALL_PROVIDERS = [
  "lexware_office",
  "sevdesk",
  "wiso",
  "easybill",
  "buchhaltungsbutler",
];

const CustomerIntegration = () => {
  const { t } = useTranslation();
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPopup, setShowPopup] = useState(false);
  const [editKeys, setEditKeys] = useState({});

  const fetchData = async () => {
    setLoading(true);
    const res = await getCustomerIntegration();
    if (res?.isSuccess) setIntegrations(res.data);
    setLoading(false);
  };

  useEffect(() => {
    // 1. Fetch data
    fetchData();

    // 2. Listen for OAuth Redirect Success/Error
    const params = new URLSearchParams(window.location.search);
    const status = params.get("status");
    const provider = params.get("provider");

    if (status === "success") {
      // You could use a toast library here
      alert(`${provider} connected successfully!`);
      // Clean the URL so the alert doesn't show again on refresh
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (status === "error") {
      alert(`Failed to connect ${provider}.`);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const handleUpdate = async (provider) => {
    const value = editKeys[provider];
    if (!value) return;

    let res;
    // Special case for Buchhaltungsbutler update
    if (provider === "buchhaltungsbutler") {
      res = await updateCustomerIntegration(
        provider,
        value.apiKey,
        integrations.find((i) => i.provider === provider).apiClient,
        value.apiSecret,
      );
    } else {
      res = await updateCustomerIntegration(provider, value);
    }

    if (res?.isSuccess) {
      setEditKeys({ ...editKeys, [provider]: "" });
      fetchData();
    }
  };

  const handleDelete = async (provider) => {
    if (window.confirm(t("integration_confirm_delete"))) {
      const res = await deleteCustomerIntegration(provider);
      if (res?.isSuccess) fetchData();
    }
  };

  // Updated to accept multiple parameters from the Modal
  const handleCreate = async (provider, apiKey, apiClient, apiSecret) => {
    const res = await addCustomerIntegration(
      provider,
      apiKey,
      apiClient,
      apiSecret,
    );
    if (res?.isSuccess) {
      setShowPopup(false);
      fetchData();
    }
  };

  const availableProviders = ALL_PROVIDERS.filter(
    (p) => !integrations.some((existing) => existing.provider === p),
  );

  return (
    <div className="copony-profile">
      <div className="integration-header">
        <Heading
          text={t("module_integrations")}
          className="copony-profile-heading"
        />
        <CommonButton
          className="stammdaten-add-btn-text"
          text={t("integration_add_btn")}
          icon={<AddCircleOutlineIcon />}
          onClick={() => setShowPopup(true)}
        ></CommonButton>
      </div>

      <div className="integration-list">
        {integrations.map((item) => (
          <div key={item._id} className="integration-box">
            <div className="info">
              <ApiIcon className="provider-icon" sx={{ color: "#1976d2" }} />
              <span className="provider-name">
                {item.provider === "lexware_office"
                  ? "Lexware Office"
                  : item.provider === "buchhaltungsbutler"
                  ? "Buchhaltungsbutler"
                  : item.provider.toUpperCase()}
              </span>
            </div>
            <div className="actions">
              <IntegrationItemActions
                item={item}
                editValue={editKeys[item.provider]}
                onEditChange={(val) =>
                  setEditKeys({ ...editKeys, [item.provider]: val })
                }
                onUpdate={handleUpdate}
                t={t}
              />
                <button
                  className="delete-btn"
                  onClick={() => handleDelete(item.provider)}
                  style={{
                    height: "fit-content",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  <img
                    src={deleteIcon}
                    alt="delete"
                    style={{ width: "20px" }}
                  />
                </button>
            </div>
          </div>
        ))}
      </div>

      <IntegrationModal
        isOpen={showPopup}
        onClose={() => setShowPopup(false)}
        onSave={handleCreate}
        availableProviders={availableProviders}
      />
    </div>
  );
};

export default CustomerIntegration;
